/* Wall-art aspect-ratio metadata.
 *
 * The cluster outputs every render in 6 aspect ratios, encoded into the
 * filename (e.g. hero-portrait_4x5.png). Each ratio maps to a customer-
 * facing label + a representative print size string for the size selector
 * on /digital-art/[slug]. */

export type AspectInfo = {
  /** CSS aspect-ratio value, e.g. "4 / 5". */
  aspect: string;
  /** Filename ratio token, e.g. "4x5". */
  ratio: string;
  /** Customer-facing label for the size pill. */
  label: string;
  /** Representative print dimension shown under the label. */
  printSize: string;
  orientation: "square" | "portrait" | "landscape" | "wide";
};

export const ASPECT_LABELS: Record<string, AspectInfo> = {
  "1x1": {
    aspect: "1 / 1",
    ratio: "1x1",
    label: "Square",
    printSize: "12 × 12 in",
    orientation: "square",
  },
  "4x5": {
    aspect: "4 / 5",
    ratio: "4x5",
    label: "Portrait 4×5",
    printSize: "16 × 20 in",
    orientation: "portrait",
  },
  "2x3": {
    aspect: "2 / 3",
    ratio: "2x3",
    label: "Portrait 2×3",
    printSize: "12 × 18 in",
    orientation: "portrait",
  },
  "3x4": {
    aspect: "3 / 4",
    ratio: "3x4",
    label: "Portrait 3×4",
    printSize: "15 × 20 in",
    orientation: "portrait",
  },
  "5x4": {
    aspect: "5 / 4",
    ratio: "5x4",
    label: "Landscape 5×4",
    printSize: "20 × 16 in",
    orientation: "landscape",
  },
  "16x9": {
    aspect: "16 / 9",
    ratio: "16x9",
    label: "Wide 16×9",
    printSize: "Digital wallpaper",
    orientation: "wide",
  },
};

const RATIO_ORDER: string[] = ["1x1", "4x5", "2x3", "3x4", "5x4", "16x9"];

/* Parse `_<W>x<H>.<ext>` out of a hero/preview URL. Returns AspectInfo if
 * the ratio is one we recognize, null otherwise. */
export function aspectFromUrl(url: string | null | undefined): AspectInfo | null {
  if (!url) return null;
  const m = url.match(/_(\d+)x(\d+)\.[a-z]+(?:\?|$)/i);
  if (!m) return null;
  const key = `${m[1]}x${m[2]}`;
  return ASPECT_LABELS[key] ?? null;
}

/* CSS aspect string fallback. Use when AspectInfo isn't available — gives
 * a reasonable default for the masonry tile sizing. */
export function aspectStringFromUrl(url: string | null | undefined): string {
  const info = aspectFromUrl(url);
  return info?.aspect ?? "4 / 5";
}

/* Sort family siblings into a consistent order for the size selector. */
export function sortByAspect<T extends { ratio: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const ai = RATIO_ORDER.indexOf(a.ratio);
    const bi = RATIO_ORDER.indexOf(b.ratio);
    if (ai === -1 && bi === -1) return a.ratio.localeCompare(b.ratio);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
}
